import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useLocation, useParams } from "react-router-dom";

import api from "../api";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import { resolveResultsChallengeId } from "./openChallengeResults";

const PageContainer = styled.div`
  min-height: 100vh;
  font-family: "Quicksand", sans-serif;
  padding: 20px;
  background: ${(props) =>
    props.isDarkMode ? "var(--background-dark)" : "var(--background-light)"};
  transition: background 0.5s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
`;


const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #dd6260;
  text-align: center;
  margin: 20px 0;
`;

const shine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const WinnerCard = styled.div`
  background: linear-gradient(135deg, #ffd700 0%, #fff6a8 50%, #ffd700 100%);
  background-size: 200% auto;
  animation: ${shine} 3s linear infinite;
  border-radius: 20px;
  padding: 30px;
  max-width: 700px;
  width: 100%;
  text-align: center;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(255, 215, 0, 0.3);
`;

const WinnerLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #806000;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 8px;
`;

const WinnerName = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  color: #4a3600;
`;

const WinnerStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 12px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: #4a3600;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #806000;
`;

const TableCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 20px;
  max-width: 700px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 100px;
`;

const TableTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
`;

const ParticipantRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 80px 80px;
  align-items: center;
  padding: 12px 10px;
  border-radius: 10px;
  margin: 4px 0;
  background: ${(props) => (props.isCurrentUser ? "#f3eefa" : props.rank <= 3 ? "#fffdf0" : "transparent")};
  border: ${(props) => (props.isCurrentUser ? "2px solid #8747d4" : "none")};

  @media (max-width: 500px) {
    grid-template-columns: 35px 1fr 60px 65px;
    padding: 10px 6px;
  }
`;

const HeaderRow = styled(ParticipantRow)`
  background: none;
  border: none;
  font-weight: 700;
  color: #888;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Rank = styled.div`
  font-weight: 800;
  font-size: 1rem;
  color: ${(props) => {
    if (props.rank === 1) return "#ffd700";
    if (props.rank === 2) return "#C0C0C0";
    if (props.rank === 3) return "#CD7F32";
    return "#888";
  }};
`;

const PlayerName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScoreCell = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  color: #8747d4;
  text-align: center;
`;

const TimeCell = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: #555;
  text-align: center;
`;


const NoWinnerCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 30px;
  max-width: 700px;
  width: 100%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  color: #555;
`;


const STATS_RETRY_DELAY_MS = 450;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function formatTime(ms) {
  if (typeof ms !== "number" || Number.isNaN(ms)) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds} sec`;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes} min ${seconds} sec`;
}

function KnowledgeChallengeResults() {
  const { isDarkMode } = useAmazonContext();
  const location = useLocation();
  const { challengeId: challengeIdParam } = useParams();
  const challengeId = resolveResultsChallengeId({
    challengeIdParam,
    search: location.search,
    state: location.state,
  });
  const preview = location.state || {};

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(Boolean(challengeId));
  const [fetchFailed, setFetchFailed] = useState(!challengeId);
  const requestSeq = useRef(0);
  const userEmail = localStorage.getItem("userEmail");

  useEffect(() => {
    const requestId = requestSeq.current + 1;
    requestSeq.current = requestId;
    let active = true;
    const stillCurrent = () => active && requestSeq.current === requestId;

    if (!challengeId) {
      setLoading(false);
      setFetchFailed(true);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setFetchFailed(false);
    setStats(null);

    const fetchData = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const statsRes = await api.get("/knowledge-challenge/stats", {
            params: { challengeId },
          });
          if (!stillCurrent()) return;
          setStats(statsRes.data);
          setFetchFailed(false);
          setLoading(false);
          return;
        } catch (err) {
          if (!stillCurrent()) return;
          if (attempt === 0) {
            await wait(STATS_RETRY_DELAY_MS);
            if (!stillCurrent()) return;
            continue;
          }
          console.error("Error fetching results:", err);
          setFetchFailed(true);
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [challengeId]);

  const previewMatches =
    preview.challengeId && String(preview.challengeId) === String(challengeId);
  const previewWinner =
    previewMatches && preview.winner && preview.winner.userEmail
      ? preview.winner
      : null;
  const winner = stats
    ? stats.winner && stats.winner.userEmail
      ? stats.winner
      : null
    : previewWinner;
  const challengeName =
    (stats && stats.name) ||
    (previewMatches && preview.name) ||
    "Knowledge Challenge";
  const participants = Array.isArray(stats && stats.participants)
    ? stats.participants
    : [];

  if (loading && !winner) {
    return (
      <PageContainer isDarkMode={isDarkMode}>
        <Title>Loading Results...</Title>
      </PageContainer>
    );
  }

  if (!winner && !stats && (fetchFailed || !challengeId)) {
    return (
      <PageContainer isDarkMode={isDarkMode}>
        <NoWinnerCard>
          <Title>Results Not Available</Title>
          <p>Could not load challenge results.</p>
        </NoWinnerCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer isDarkMode={isDarkMode}>
      <Title>{challengeName}</Title>
      {winner ? (
        <WinnerCard>
          <WinnerLabel>Winner</WinnerLabel>
          <WinnerName>
            🏆 {winner.givenName} {winner.familyName}
          </WinnerName>
          <WinnerStats>
            <StatItem>
              <StatValue>{winner.score}</StatValue>
              <StatLabel>Score</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{formatTime(winner.timeTaken)}</StatValue>
              <StatLabel>Time</StatLabel>
            </StatItem>
          </WinnerStats>
        </WinnerCard>
      ) : null}

      {participants.length > 0 ? (
        <TableCard>
          <WinnerLabel style={{ display: "flex", justifyContent: "center" }}>All Participants</WinnerLabel>
          <HeaderRow>
            <div>#</div>
            <div>Name</div>
            <div style={{ textAlign: "center" }}>Score</div>
            <div style={{ textAlign: "center" }}>Time</div>
          </HeaderRow>
          {participants.map((p, idx) => {
            const isCurrentUser = p.userEmail === userEmail;
            return (
              <ParticipantRow key={p._id || idx} rank={idx + 1} isCurrentUser={isCurrentUser}>
                <Rank rank={idx + 1}>
                  {idx + 1 === 1 ? "🥇" : idx + 1 === 2 ? "🥈" : idx + 1 === 3 ? "🥉" : idx + 1}
                </Rank>
                <PlayerName>
                  {p.givenName} {p.familyName}
                </PlayerName>
                <ScoreCell>
                  {p.score}/{p.totalQuestions}
                </ScoreCell>
                <TimeCell>{formatTime(p.timeTaken)}</TimeCell>
              </ParticipantRow>
            );
          })}
        </TableCard>
      ) : loading ? (
        <p>Loading participants...</p>
      ) : null}
    </PageContainer>
  );
}

export default KnowledgeChallengeResults;
